import { useForm } from "react-hook-form";
import { useState, useEffect } from "react";
import { getApi } from '../../providers/AuthContext';
import { PapErrors, PapInput } from "../FormElements";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import { motion, AnimatePresence } from "framer-motion";
import { CheckCircle2, Mail, KeyRound, ArrowRight } from "lucide-react";

interface AccountFormProps {
  user: any;
  confirmed: boolean;
}

const schema = yup.object().shape({
  email: yup.string().email('Please enter a valid email').required('Email is required'),
});

const AccountForm: React.FC<AccountFormProps> = ({ user, confirmed }) => {
  const api = getApi();
  const [pageErrors, setPageErrors] = useState<string[]>([]);
  const [passwordErrors, setPasswordErrors] = useState<string[]>([]);
  const [passwordSuccess, setPasswordSuccess] = useState(false);
  const [emailUpdateRequested, setEmailUpdateRequested] = useState(false);

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors }
  } = useForm({
    resolver: yupResolver(schema)
  });

  const resetErrors = () => {
    setPageErrors([]);
  };

  const resetPage = () => {
    reset();
    resetErrors();
  };

  useEffect(() => {
    if (user) {
      reset(user);
    }
  }, [user]);

  const resetPassword = async () => {
    const data = {
      email: user.email
    };

    try {
      const result = await api.post('/api/v1/passwords', data);
      if (result) {
        setPasswordErrors([]);
        setPasswordSuccess(true);
      }
    } catch (ex: any) {
      if (ex.response) {
        const exResponseBody = await ex.response.json();
        setPasswordErrors([exResponseBody.error]);
      }
    }
  };

  const saveUser = async (data: any) => {
    const user_data = {
      user: data
    };

    let result = null;
    try {
      result = await api.put('/api/v1/users/me', user_data);
      setEmailUpdateRequested(true);
    } catch (ex: any) {
      setPageErrors([ex.response?.error || 'An error occurred']);
    }

    if (result) {
      resetPage();
    }
  };

  return (
    <div>
      {/* Success alerts */}
      <AnimatePresence>
        {confirmed && (
          <motion.div
            initial={{ opacity: 0, y: -8 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -8 }}
            className="flex items-center gap-2.5 p-3.5 bg-success/[0.06] dark:bg-success/[0.1] border border-success/20 rounded-xl mb-5 text-sm text-success"
          >
            <div className="w-7 h-7 rounded-lg bg-success/10 flex items-center justify-center flex-shrink-0">
              <CheckCircle2 className="w-4 h-4" />
            </div>
            <span className="font-medium">Your email change has been confirmed.</span>
          </motion.div>
        )}
      </AnimatePresence>

      <AnimatePresence>
        {emailUpdateRequested && (
          <motion.div
            initial={{ opacity: 0, y: -8 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -8 }}
            className="flex items-center gap-2.5 p-3.5 bg-accent/[0.06] dark:bg-accent/[0.1] border border-accent/20 rounded-xl mb-5 text-sm text-accent"
          >
            <div className="w-7 h-7 rounded-lg bg-accent/10 flex items-center justify-center flex-shrink-0">
              <Mail className="w-4 h-4" />
            </div>
            <span className="font-medium">Please check your email to confirm your email change.</span>
          </motion.div>
        )}
      </AnimatePresence>

      <PapErrors errors={pageErrors} />

      {/* Email section */}
      <form noValidate onSubmit={handleSubmit(saveUser)}>
        <PapInput fieldLabel="Email" type="email" formBinding={register("email")} required={true} fieldName="email" error={errors.email} />
        <div className="mt-1">
          <button type="submit" className="btn btn-primary text-sm">
            <Mail className="w-4 h-4 mr-1.5" />
            Update Email
          </button>
        </div>
      </form>

      {/* Password section */}
      <div className="mt-6 pt-6 border-t border-border/40">
        <div className="flex items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-xl bg-muted/60 dark:bg-muted/30 flex items-center justify-center flex-shrink-0">
              <KeyRound className="w-4 h-4 text-muted-foreground" />
            </div>
            <div>
              <h3 className="text-sm font-bold text-foreground m-0">Password</h3>
              <p className="text-xs text-muted-foreground m-0 mt-0.5">Receive a link to reset your password</p>
            </div>
          </div>
          <button onClick={resetPassword} className="btn btn-light text-sm">
            Change Password
            <ArrowRight className="w-3.5 h-3.5 ml-1.5" />
          </button>
        </div>
        <PapErrors errors={passwordErrors} />
        <AnimatePresence>
          {passwordSuccess && (
            <motion.div
              initial={{ opacity: 0, y: -8 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -8 }}
              className="flex items-center gap-2.5 p-3.5 bg-success/[0.06] dark:bg-success/[0.1] border border-success/20 rounded-xl mt-4 text-sm text-success"
            >
              <div className="w-7 h-7 rounded-lg bg-success/10 flex items-center justify-center flex-shrink-0">
                <CheckCircle2 className="w-4 h-4" />
              </div>
              <span className="font-medium">Check your email for password reset instructions.</span>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
};

export default AccountForm;
